import { useState, useEffect, useRef } from 'preact/hooks';
import { html, Modal, Field, closeModal, openModal, notify } from './common.js';
import { get, post } from '../api.js';
import { geology, me, tab, modalStack, picking, toast, selection, viewer, model, basemaps, basemap, places, modelStatus, pollModel, measure, isAdmin, canWrite, feat, slicer, setSlicer, surveyData } from '../store.js';
import { GroundPanel, PassagesPanel, GeologyTab, WaterPanel, OverlaysPanel, LabelsPanel, SurveysPanel, PlacesPanel, StudiesPanel, TripsPanel } from './panels.js';
import { Drawer } from './details.js';
import { FeatureForm, ObservationForm, StudyForm, PointForm, TripForm, LinkPicker, SurveyUpload, UploadSummary } from './forms.js';
import { AdminModal } from './admin.js';
import { PasswordModal } from './auth.js';
import { CreditsModal, HowtoModal } from './credits.js';
import { CursorReadout, MeasurePanel, SinksPanel, ParcelTagForm } from './tools.js';
import { CavesPanel, CaveForm } from './caves.js';
import { PlaceForm, PlaceReview } from './places.js';
import { LibraryModal, openLibrary } from './library.js';
import { FindingsReview } from './findings.js';
import { SlicerPanel, ColorsModal } from './display.js';
import { ProfileButton, ProfilePanel } from './profile.js';
import { XSectionModal, ReadingsModal } from './xsection.js';
import { WaterModal, RouteForm, LinkForm } from './water.js';
import { DemoWelcome, maybeWelcome } from './demo.js';
import { RecordsList, RecordsImport, PoiForm, CategoryForm, PoiPanel } from './records.js';
import { SurveyDataPanel } from './surveydata.js';
import { SinkAnalysisModal } from './sinkanalysis.js';

// two rows of tabs: what the 3D view shows, then the lists.  [key, label, feature switch it needs]
const VIEW_TABS = [['ground', 'Ground'], ['passages', 'Passages'], ['geology', 'Geology', 'geology'], ['water', 'Water', 'hydrology'], ['overlays', 'Overlays'], ['labels', 'Labels']];
const LIST_TABS = [['caves', 'Caves', 'cavepages'], ['surveys', 'Surveys'], ['places', 'Places'], ['poi', 'POI', 'records'], ['sinks', 'Sinks', 'sinks'], ['studies', 'Studies', 'studies'], ['trips', 'Trips', 'trips']];
const tabOn = ([k, , f]) => (!f || feat(f)) && (k !== 'geology' || !!geology.value);
function TabRow({ list, cls }) {
  return html`<nav class=${'tabs ' + cls} role="tablist">${list.filter(tabOn).map(([k, l]) => html`<button role="tab" aria-selected=${tab.value === k}
    class=${'tab' + (tab.value === k ? ' active' : '')} onClick=${() => tab.value = k}>${l}</button>`)}</nav>`;
}

function Rose() {
  const ref = useRef();
  useEffect(() => { viewer.on('camera', (deg) => { if (ref.current) ref.current.style.transform = `rotate(${deg}deg)`; }); }, []);
  return html`<svg ref=${ref} width="26" height="26" viewBox="-14 -14 28 28" aria-label="North arrow"><circle r="13" fill="none" stroke="rgba(255,255,255,.2)" />
    <path d="M0,-11 L4,2 L0,0 L-4,2 Z" fill="#f07d4c" /><text y="10" text-anchor="middle" font-size="7" fill="#c3c2b7">N</text></svg>`;
}

function Busy() {                          // "loading" hint while terrain or map tiles stream in
  const ref = useRef();
  useEffect(() => { viewer.on('busy', (b) => { if (ref.current) ref.current.style.display = b ? '' : 'none'; }); }, []);
  return html`<span ref=${ref} class="muted small" style="display:none" aria-live="polite">Loading map…</span>`;
}

// The whole atlas as one styled GeoPackage, built on the server for this member (atlas/export_gpkg.py)
let exporting = false;
async function downloadGpkg() {
  if (exporting) return;
  exporting = true;
  notify('Building the GeoPackage… it downloads in about half a minute.');
  try {
    const r = await fetch('/api/export.gpkg', { credentials: 'same-origin' });
    if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || `error ${r.status}`);
    const name = /filename="?([^";]+)/.exec(r.headers.get('content-disposition') || '')?.[1] || 'bccs-atlas.gpkg';
    const a = document.createElement('a');
    a.href = URL.createObjectURL(await r.blob()); a.download = name; a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 60000);
  } catch (e) {
    notify(`GeoPackage: ${e.message}`, 'err');
  } finally {
    exporting = false;
  }
}

export function App() {
  useEffect(() => { maybeWelcome(); }, []);
  useEffect(() => {
    const k = (e) => { if (e.key === 'Escape' && measure.value.on) { viewer.stopMeasure(); measure.value = { ...measure.value, on: false }; } };
    window.addEventListener('keydown', k); return () => window.removeEventListener('keydown', k);
  }, []);
  useEffect(() => {
    if (!isAdmin()) return;
    const t = setInterval(() => pollModel().catch(() => {}), 15000); return () => clearInterval(t);
  }, []);
  const bm = basemaps.value.find(b => b.key === basemap.value);
  const tabDef = [...VIEW_TABS, ...LIST_TABS].find(([k]) => k === tab.value);
  if (!tabDef || !tabOn(tabDef)) tab.value = 'ground';
  const T = tab.value;
  return html`
    <${TopBar} />
    <aside class="side" aria-label="Layers and lists">
      <${TabRow} list=${VIEW_TABS} cls="tabs-view" /><${TabRow} list=${LIST_TABS} cls="tabs-list" />
      <div class="body">
        ${T === 'ground' && html`<${GroundPanel} />`}${T === 'passages' && html`<${PassagesPanel} />`}${T === 'geology' && html`<${GeologyTab} />`}
        ${T === 'water' && html`<${WaterPanel} />`}${T === 'overlays' && html`<${OverlaysPanel} />`}${T === 'labels' && html`<${LabelsPanel} />`}
        ${tab.value === 'caves' && html`<${CavesPanel} />`}${tab.value === 'surveys' && html`<${SurveysPanel} />`}
        ${tab.value === 'places' && html`<${PlacesPanel} />`}${tab.value === 'poi' && html`<${PoiPanel} />`}${tab.value === 'sinks' && html`<${SinksPanel} />`}${tab.value === 'studies' && html`<${StudiesPanel} />`}
        ${tab.value === 'trips' && html`<${TripsPanel} />`}
      </div>
    </aside>
    <${Drawer} />
    <div class="hud">
      <${Rose} />
      <span class="seg" style="display:inline-grid;grid-auto-columns:auto">${[['oblique', 'Oblique'], ['plan', 'Plan'], ['profile', 'Profile'], ['area', 'Whole area']].map(([k, l]) => html`
        <button onClick=${() => viewer.view(k)}>${l}</button>`)}</span>
      ${feat('measure') && html`<button class=${'btn sm' + (measure.value.on ? ' primary' : '')} style="font-size:12px;padding:3px 8px"
        onClick=${() => { if (measure.value.on) { viewer.stopMeasure(); measure.value = { ...measure.value, on: false }; } else { viewer.startMeasure(); measure.value = { ...measure.value, on: true }; } }}
        title="Measure distances, heights and bearings">📏 Measure</button>`}
      ${feat('slicer') && model.value && html`<button class=${'btn sm' + (slicer.value.on ? ' primary' : '')} style="font-size:12px;padding:3px 8px"
        onClick=${() => { const c = slicer.value; setSlicer(!c.on ? { on: true, open: true } : !c.open ? { open: true } : { on: false, open: false }); }}
        title=${slicer.value.on && !slicer.value.open ? 'Show the slicer' : slicer.value.on ? 'Turn the slicer off' : 'Cut the model away to look at cross-sections'}>✂ Slice</button>`}
      ${feat('profiles') && html`<${ProfileButton} />`}
      <${CursorReadout} />
      <span class="tiny" style="opacity:.8">${bm?.attribution || ''}</span>
      <button class="btn ghost sm" style="font-size:11.5px;padding:2px 6px" onClick=${() => openModal('credits')}>Credits</button><${Busy} />
    </div>
    <${MeasurePanel} />
    ${feat('profiles') && html`<${ProfilePanel} />`}
    ${surveyData.value && html`<${SurveyDataPanel} />`}
    ${feat('slicer') && html`<${SlicerPanel} />`}
    ${picking.value && html`<div class="picking">${picking.value.label}<button class="btn sm" onClick=${() => { viewer.cancelPick(); picking.value = null; }}>Cancel</button></div>`}
    ${modalStack.value.map(m => html`<${ModalHost} key=${m.key} m=${m} />`)}
    ${toast.value && html`<div class="toast" role="status" style=${toast.value.kind === 'err' ? 'border-color:rgba(230,103,103,.5)' : ''}>${toast.value.msg}</div>`}`;
}

function ModalHost({ m }) {
  const p = m.props;
  switch (m.kind) {
    case 'feature': return html`<${FeatureForm} ...${p} />`;
    case 'observation': return html`<${ObservationForm} ...${p} />`;
    case 'study': return html`<${StudyForm} ...${p} />`;
    case 'point': return html`<${PointForm} ...${p} />`;
    case 'trip': return html`<${TripForm} ...${p} />`;
    case 'linkpicker': return html`<${LinkPicker} ...${p} />`;
    case 'upload': return html`<${SurveyUpload} />`;
    case 'filesummary': return html`<${Modal} wide title=${p.sf.name}><${UploadSummary} sf=${p.sf} /><//>`;
    case 'admin': return html`<${AdminModal} ...${p} />`;
    case 'password': return html`<${PasswordModal} />`;
    case 'credits': return html`<${CreditsModal} />`;
    case 'howto': return html`<${HowtoModal} />`;
    case 'parceltag': return html`<${ParcelTagForm} ...${p} />`;
    case 'cave': return html`<${CaveForm} ...${p} />`;
    case 'place': return html`<${PlaceForm} ...${p} />`;
    case 'placereview': return html`<${PlaceReview} ...${p} />`;
    case 'library': return html`<${LibraryModal} ...${p} />`;
    case 'findings': return html`<${FindingsReview} ...${p} />`;
    case 'colors': return html`<${ColorsModal} ...${p} />`;
    case 'xsection': return html`<${XSectionModal} ...${p} />`;
    case 'readings': return html`<${ReadingsModal} ...${p} />`;
    case 'water': return html`<${WaterModal} ...${p} />`;
    case 'demowelcome': return html`<${DemoWelcome} />`;
    case 'records': return html`<${RecordsList} />`;
    case 'recordsimport': return html`<${RecordsImport} />`;
    case 'poiform': return html`<${PoiForm} ...${p} />`;
    case 'poicategory': return html`<${CategoryForm} ...${p} />`;
    case 'sinkanalysis': return html`<${SinkAnalysisModal} />`;
    case 'waterroute': return html`<${RouteForm} ...${p} />`;
    case 'waterlink': return html`<${LinkForm} ...${p} />`;
    default: return null;
  }
}

function TopBar() {
  const [menu, setMenu] = useState(null);
  const u = me.value, write = canWrite();
  const pending = modelStatus.value?.pending_uploads;
  useEffect(() => { const c = () => setMenu(null); window.addEventListener('click', c); return () => window.removeEventListener('click', c); }, []);
  const toggle = (k) => (e) => { e.stopPropagation(); setMenu(menu === k ? null : k); };
  return html`<header class="topbar">
    <div class="brand"><div class="logo">${(u.site_name || 'A').trim()[0].toUpperCase()}</div><span class="full">${u.site_name || 'Cave Atlas'}</span>
      ${u.demo && html`<button class="pill demo-pill" title="Everything here is made up. Click for what to try." onClick=${(e) => { e.stopPropagation(); openModal('demowelcome'); }}>DEMO</button>`}</div>
    <${Search} />
    <div class="spacer"></div>
    ${feat('library') && html`<button class="btn" onClick=${() => openLibrary()} title="Newsletters: search and read" aria-label="Library">📚 <span class="full">Library</span></button>`}
    <button class="btn" onClick=${() => openModal('howto')} title="How to use the atlas" aria-label="How to use the atlas">? <span class="full">Help</span></button>
    ${write && html`<div style="position:relative"><button class="btn primary" onClick=${toggle('add')} aria-haspopup="menu">＋ Add</button>
      ${menu === 'add' && html`<div class="results" style="left:auto;right:0;width:230px" role="menu">
        ${[['feature', 'Surface feature (sinkhole, dig…)'], ['observation', 'Observation (bats, water…)', 'observations'], ['study', 'Study (dye / air trace)', 'studies'],
          ['trip', 'Trip report', 'trips'], ['upload', 'Compass survey (.dat)']].filter(([, , f]) => !f || feat(f)).map(([k, l]) => html`<button role="menuitem" onClick=${() => openModal(k)}>${l}</button>`)}</div>`}</div>`}
    <div style="position:relative"><button class="btn" onClick=${toggle('user')} aria-haspopup="menu">${u.name} <span class="pill">${u.role}</span>
      ${isAdmin() && pending ? html`<span class="pill warn">${pending}</span>` : ''}</button>
      ${menu === 'user' && html`<div class="results" style="left:auto;right:0;width:210px" role="menu">
        ${isAdmin() && html`<button role="menuitem" onClick=${() => openModal('admin', { tab: pending ? 'uploads' : 'members' })}>Admin${pending ? ` (${pending} to review)` : ''}</button>`}
        <button role="menuitem" onClick=${() => openModal('howto')}>How to use the atlas</button>
        <button role="menuitem" onClick=${() => openModal('credits')}>Credits & sources</button>
        ${!u.visitor && html`<button role="menuitem" onClick=${() => { setMenu(null); downloadGpkg(); }}>Download GeoPackage (QGIS)</button>`}
        ${u.visitor ? html`<button role="menuitem" onClick=${() => openModal('demowelcome')}>About this demo</button>
          <button role="menuitem" onClick=${() => { location.hash = '#/login'; location.reload(); }}>Sign in (admin)</button>`
        : html`<button role="menuitem" onClick=${() => openModal('password')}>Change password</button>
          <button role="menuitem" onClick=${async () => { await post('/api/auth/logout'); location.reload(); }}>Sign out</button>`}
        <div class="tiny muted" style="padding:6px 10px 4px;border-top:1px solid var(--line)">Version ${typeof __ATLAS_BUILD__ !== 'undefined' ? __ATLAS_BUILD__ : 'dev'}</div></div>`}</div>
  </header>`;
}

function Search() {
  const [q, setQ] = useState(''), [res, setRes] = useState([]), [open, setOpen] = useState(false), [act, setAct] = useState(0);
  const box = useRef();
  useEffect(() => {
    if (q.trim().length < 2) { setRes([]); return; }
    const t = setTimeout(async () => { try { setRes(await get(`/api/search?q=${encodeURIComponent(q)}`)); setAct(0); setOpen(true); } catch { /* ignore */ } }, 180);
    return () => clearTimeout(t);
  }, [q]);
  const go = (r) => {
    setOpen(false);
    if (r.type === 'station') { const s = viewer.flyToStation(r.id); if (s) selection.value = s; }
    else if (r.type === 'cave' || r.type === 'place') selection.value = { type: r.type, id: r.id };
    else {
      const p = places.value.find(x => x.type === r.type && x.id === r.id);
      selection.value = { type: r.type, id: r.id, place: p };
    }
  };
  return html`<div class="search" ref=${box} onClick=${(e) => e.stopPropagation()}>
    <span class="ico">⌕</span>
    <input type="search" placeholder="Search caves, features, observations, trips, stations…" value=${q} aria-label="Search"
      onInput=${(e) => setQ(e.target.value)} onFocus=${() => setOpen(true)}
      onKeyDown=${(e) => { if (e.key === 'ArrowDown') setAct(Math.min(res.length - 1, act + 1)); if (e.key === 'ArrowUp') setAct(Math.max(0, act - 1));
        if (e.key === 'Enter' && res[act]) go(res[act]); if (e.key === 'Escape') setOpen(false); }} />
    ${open && (res.length > 0 || (q.trim().length > 2 && feat('library'))) && html`<div class="results">${res.map((r, i) => html`<button class=${i === act ? 'active' : ''} onClick=${() => go(r)}>
      <span class="pill">${r.type.replace('_', ' ')}</span><span style="flex:1">${r.label}</span><span class="muted small">${r.sub || ''}</span></button>`)}
      ${q.trim().length > 2 && feat('library') && html`<button onClick=${() => { setOpen(false); openLibrary({ q: q.trim() }); }}>
        <span class="pill">library</span><span style="flex:1">Search the newsletters for “${q.trim()}”</span></button>`}</div>`}
  </div>`;
}
